import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { landingTemplateAPI, landingPageAPI, branchAPI } from '../../services/api';
import { useAuthStore } from '../../store';

const CATEGORIES = ['hospitality', 'minimal', 'custom'];

export default function LandingTemplates() {
    const navigate = useNavigate();
    const admin = useAuthStore((s) => s.admin);
    const isSuperadmin = admin?.role === 'superadmin';

    const [templates, setTemplates] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoryFilter, setCategoryFilter] = useState('');

    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [previewDetail, setPreviewDetail] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const [useTarget, setUseTarget] = useState(null);
    const [useForm, setUseForm] = useState({ title: '', branchId: '' });
    const [using, setUsing] = useState(false);

    const [duplicating, setDuplicating] = useState(null);

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (categoryFilter) params.category = categoryFilter;
            const res = await landingTemplateAPI.getAll(params);
            setTemplates(res.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch templates');
        } finally {
            setLoading(false);
        }
    }, [categoryFilter]);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    useEffect(() => {
        branchAPI.getAll(isSuperadmin ? undefined : admin?.tenantId)
            .then((res) => setBranches(res.data.data?.branches || res.data.data || []))
            .catch(() => {});
    }, [admin, isSuperadmin]);

    async function openPreview(template) {
        setPreviewTemplate(template);
        setPreviewDetail(null);
        setPreviewLoading(true);
        try {
            const res = await landingTemplateAPI.getById(template._id);
            setPreviewDetail(res.data.data);
        } catch (error) {
            toast.error('Failed to load template preview');
        } finally {
            setPreviewLoading(false);
        }
    }

    function openUseModal(template) {
        setUseTarget(template);
        setUseForm({ title: `${template.title}`, branchId: '' });
    }

    async function handleUse(e) {
        e.preventDefault();

        // Superadmin has no tenant of their own, so the branch picker doubles
        // as the tenant picker: derive tenantId from the selected branch
        // (branchAPI.getAll populates it as { _id, name, slug, type }).
        let tenantId;
        if (isSuperadmin) {
            if (!useForm.branchId) {
                toast.warning('Select a branch — as superadmin, this is how the page\'s tenant is determined');
                return;
            }
            const branch = branches.find((b) => b._id === useForm.branchId);
            tenantId = branch?.tenantId?._id || branch?.tenantId;
            if (!tenantId) {
                toast.error('Could not determine the tenant for the selected branch');
                return;
            }
        }

        setUsing(true);
        try {
            const payload = { title: useForm.title || undefined };
            if (useForm.branchId) payload.branchId = useForm.branchId;
            if (tenantId) payload.tenantId = tenantId;
            const res = await landingTemplateAPI.use(useTarget._id, payload);
            toast.success('Template used — a new draft was created');
            setUseTarget(null);
            navigate(`/landing-pages/${res.data.data._id}/edit`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to use template');
        } finally {
            setUsing(false);
        }
    }

    async function handleDuplicate(template) {
        setDuplicating(template._id);
        try {
            await landingPageAPI.duplicate(template._id);
            toast.success('Template duplicated');
            fetchTemplates();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to duplicate template');
        } finally {
            setDuplicating(null);
        }
    }

    function countByType(nodes) {
        const counts = {};
        function walk(list) {
            (list || []).forEach((n) => {
                counts[n.type] = (counts[n.type] || 0) + 1;
                if (n.children) walk(n.children);
            });
        }
        walk(nodes);
        return counts;
    }

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-th-large"></i>
                            Landing Page Templates
                        </h1>
                        <p className="page-subtitle-modern">Start from a shared template instead of a blank page</p>
                    </div>
                    <button className="btn-secondary-modern" onClick={() => navigate('/landing-pages')}>
                        <i className="fas fa-arrow-left"></i> Back to Landing Pages
                    </button>
                </div>
            </div>

            <div className="filters-section">
                <div className="filter-group">
                    <select className="filter-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                        <option value="">All Categories</option>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading templates...</p>
                </div>
            ) : templates.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-th-large"></i></div>
                    <h3>No Templates Yet</h3>
                    <p>
                        {isSuperadmin
                            ? 'Save any landing page as a shared template to populate this library.'
                            : 'No shared templates are available yet — check back later or start from a blank page.'}
                    </p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {templates.map((template) => (
                        <div key={template._id} className="resource-card-modern">
                            <div className="card-header">
                                <div className="resource-title">
                                    <h3 title={template.title}>{template.title}</h3>
                                    <span className="resource-subtitle">{template.templateCategory || 'custom'}</span>
                                </div>
                            </div>
                            {template.description && (
                                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '8px 0' }}>
                                    {template.description}
                                </p>
                            )}
                            <div className="card-actions" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                <button className="action-btn-icon" title="Preview" onClick={() => openPreview(template)}>
                                    <i className="fas fa-eye"></i>
                                </button>
                                <button className="btn-primary-modern" style={{ padding: '6px 14px' }} onClick={() => openUseModal(template)}>
                                    <i className="fas fa-plus"></i> Use Template
                                </button>
                                {isSuperadmin && (
                                    <button
                                        className="action-btn-icon"
                                        title="Duplicate template"
                                        onClick={() => handleDuplicate(template)}
                                        disabled={duplicating === template._id}
                                    >
                                        <i className="fas fa-copy"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {previewTemplate && (
                <div className="modal-overlay-modern" onClick={() => setPreviewTemplate(null)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div><h2>{previewTemplate.title}</h2></div>
                            <button className="modal-close" onClick={() => setPreviewTemplate(null)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="modal-body-modern">
                            {previewLoading ? (
                                <div className="loading-modern"><div className="spinner-modern"></div></div>
                            ) : previewDetail ? (
                                <>
                                    {previewDetail.description && <p>{previewDetail.description}</p>}
                                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                        {previewDetail.nodeCount} component{previewDetail.nodeCount === 1 ? '' : 's'} total
                                    </p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                                        {Object.entries(countByType(previewDetail.nodes)).map(([type, count]) => (
                                            <span key={type} className="status-badge inactive">{type} × {count}</span>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <p>Failed to load preview.</p>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setPreviewTemplate(null)}>Close</button>
                            <button className="btn-primary-modern" onClick={() => { setPreviewTemplate(null); openUseModal(previewTemplate); }}>
                                <i className="fas fa-plus"></i> Use Template
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {useTarget && (
                <div className="modal-overlay-modern" onClick={() => setUseTarget(null)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div><h2>Use "{useTarget.title}"</h2></div>
                            <button className="modal-close" onClick={() => setUseTarget(null)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleUse}>
                            <div className="modal-body-modern">
                                <div className="form-group">
                                    <label>Title</label>
                                    <input
                                        className="form-control"
                                        value={useForm.title}
                                        onChange={(e) => setUseForm((f) => ({ ...f, title: e.target.value }))}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Branch{isSuperadmin ? ' (required)' : ''}</label>
                                    <select
                                        className="form-control"
                                        value={useForm.branchId}
                                        onChange={(e) => setUseForm((f) => ({ ...f, branchId: e.target.value }))}
                                        required={isSuperadmin}
                                    >
                                        <option value="">{isSuperadmin ? 'Select a branch...' : 'Tenant-wide (no specific branch)'}</option>
                                        {branches.map((b) => (
                                            <option key={b._id} value={b._id}>
                                                {b.name}{isSuperadmin && b.tenantId?.name ? ` — ${b.tenantId.name}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setUseTarget(null)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern" disabled={using}>
                                    {using ? 'Creating...' : 'Create & Open Builder'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
